import type { ReactNode } from 'react';
import { Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';

export interface CardTab {
  key: string;
  label: string;
  caption?: string;
  /** Npr. info ikona s tooltipom ili status badge — lijevo u zaglavlju taba, iza opisa (ako ga ima). */
  captionInfo?: ReactNode;
  headerRight?: ReactNode;
  content: ReactNode;
  /** Vidljiv, ali neklikabilan tab — za najavu funkcionalnosti koja dolazi u idućoj fazi. */
  disabled?: boolean;
}

interface CardTabsProps {
  tabs: CardTab[];
  active: string;
  onChange: (key: string) => void;
}

export const CardTabs = ({ tabs, active, onChange }: CardTabsProps) => {
  const { t } = useTranslation();
  const activeTab = tabs.find((tab) => tab.key === active) ?? tabs[0];

  return (
    <Stack gap={16} w="100%">
      <Group
        gap={4}
        wrap="nowrap"
        className="card-tabs-row"
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          boxShadow: 'inset 0 -1px 0 var(--color-border-soft)'
        }}
      >
        {tabs.map((tab) => {
          const isActive = tab.key === activeTab.key;
          const label = (
            <Text
              key={tab.key}
              role={tab.disabled ? undefined : 'button'}
              tabIndex={tab.disabled ? undefined : 0}
              className="card-tab"
              data-active={isActive || undefined}
              data-disabled={tab.disabled || undefined}
              onClick={() => !tab.disabled && onChange(tab.key)}
              onKeyDown={(event) => {
                if (!tab.disabled && (event.key === 'Enter' || event.key === ' '))
                  onChange(tab.key);
              }}
            >
              {tab.label}
            </Text>
          );
          return tab.disabled ? (
            <Tooltip key={tab.key} label={t('common.comingSoon')}>
              {label}
            </Tooltip>
          ) : (
            label
          );
        })}
      </Group>

      {(activeTab.caption || activeTab.captionInfo || activeTab.headerRight) && (
        <Group
          justify="space-between"
          align="center"
          wrap="wrap"
          gap={12}
          w="100%"
          visibleFrom={activeTab.caption || activeTab.captionInfo ? undefined : 'sm'}
        >
          {activeTab.caption || activeTab.captionInfo ? (
            <Group gap={6} wrap="nowrap">
              {activeTab.caption && (
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {activeTab.caption}
                </Text>
              )}
              {activeTab.captionInfo}
            </Group>
          ) : (
            <div />
          )}
          {activeTab.headerRight && <Box visibleFrom="sm">{activeTab.headerRight}</Box>}
        </Group>
      )}
      {activeTab.headerRight && (
        <Box hiddenFrom="sm" className="card-tab-actions">
          {activeTab.headerRight}
        </Box>
      )}

      <div
        className="card-tabs-content"
        style={{
          borderRadius: 12,
          backgroundColor: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border-default)',
          overflow: 'hidden'
        }}
      >
        {activeTab.content}
      </div>
    </Stack>
  );
};
